import React from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import AddWorkflowModal, {
  TabContentHelpers
} from "@/pages/settings/components/shared/stageStudio/addWorkflow/AddWorkflowModal";
import { WorkflowDetailsFormData } from "@/pages/settings/components/shared/stageStudio/addWorkflow/types";
import {
  useAllOnboardingWorkflows,
  useCreateOnboardingWorkflow,
  useOnboardingStages
} from "@/pages/settings/components/contractsSettings/hooks";
import {
  OnboardingStage,
  OnboardingStageWorkflow
} from "@/pages/settings/components/contractsSettings/types";
import WorkflowTypesContent from "./WorkflowTypesContent";
import OnboardingWorkflowList from "./OnboardingWorkflowList";
import { ContractsWorkflowType } from "./types";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  stageId?: string;
}

const OnboardingAddWorkflowModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  stageId
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const createOnboardingWorkflow = useCreateOnboardingWorkflow();
  const { data: allWorkflowsData } = useAllOnboardingWorkflows(isOpen);
  const { data: stagesData } = useOnboardingStages();

  const systemWorkflows =
    allWorkflowsData?.workflows?.filter(
      (workflow: OnboardingStageWorkflow) => workflow.isSystem === true
    ) || [];
  const userWorkflows =
    allWorkflowsData?.workflows?.filter(
      (workflow: OnboardingStageWorkflow) => workflow.isSystem !== true
    ) || [];

  const tabOptions: Record<string, string> = {
    "workflow-types": t("workflows.tabs.workflowTypes"),
    "out-of-the-box": t("workflows.tabs.outOfTheBox"),
    "your-workflows": t("workflows.tabs.yourWorkflows")
  };

  const workflowTypeOptions = [
    {
      id: String(ContractsWorkflowType.GENERAL),
      displayName: t("workflows.types.general"),
      disabled: false
    },
    {
      id: String(ContractsWorkflowType.YES_NO_QUESTIONNAIRE),
      displayName: t("workflows.types.yesNoQuestionnaire"),
      disabled: false
    },
    {
      id: String(ContractsWorkflowType.CHECKLIST),
      displayName: t("workflows.types.checklist"),
      disabled: false
    }
  ];

  const stageOptions = (stagesData?.stages || []).map(
    (stage: OnboardingStage) => ({
      id: stage.id,
      displayName: stage.name
    })
  );

  const handleSubmit = async (data: WorkflowDetailsFormData) => {
    try {
      const response = await createOnboardingWorkflow.mutateAsync({
        data: {
          name: data.workflowName,
          description: data.workflowDescription || null,
          workflowType: Number(data.workflowType),
          contractsOnboardingStageId: data.workflowStage
        }
      });
      queryClient.invalidateQueries({
        queryKey: ["settings/contracts/stages/stageById"]
      });
      setIsOpen(false);
      if (response?.id) {
        navigate(
          `/settings/contracts/stages/${data.workflowStage}/${response.id}${location.search}`
        );
      }
    } catch (error) {
      console.error("Failed to create onboarding workflow:", error);
      toast.error(t("workflows.modal.createError"));
    }
  };

  const renderTabContent = (
    activeTab: string,
    { selectType, close }: TabContentHelpers
  ) => {
    switch (activeTab) {
      case "out-of-the-box":
        return (
          <OnboardingWorkflowList
            workflows={systemWorkflows}
            currentStageId={stageId}
            onAssigned={close}
            emptyKey="workflows.tabs.emptyOutOfTheBox"
          />
        );
      case "your-workflows":
        return (
          <OnboardingWorkflowList
            workflows={userWorkflows}
            currentStageId={stageId}
            onAssigned={close}
            emptyKey="workflows.tabs.emptyYourWorkflows"
          />
        );
      case "workflow-types":
      default:
        return (
          <WorkflowTypesContent
            onStartBuilding={(type) => selectType(String(type))}
          />
        );
    }
  };

  return (
    <AddWorkflowModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      stageId={stageId}
      defaultTab="workflow-types"
      tabOptions={tabOptions}
      renderTabContent={renderTabContent}
      workflowTypeOptions={workflowTypeOptions}
      stageOptions={stageOptions}
      onSubmit={handleSubmit}
      isSubmitting={createOnboardingWorkflow.isPending}
    />
  );
};

export default OnboardingAddWorkflowModal;
